'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';
import { rainbowBorderBackground } from './ledger-rainbow';

/** Secondary engagement block -- moved out of the hero so the primary conversion
 * flow (handle input / Claim My Ledger / See How It Works) stays uncluttered. */
export default function EraSection() {
  const { safeT } = useTranslation();
  const router = useRouter();
  const [selectedEra, setSelectedEra] = useState<'90s' | '00s' | '10s'>('90s');

  const eraOptions = [
    { id: '90s' as const, label: safeT('landing_hero.era_90s', '90s Kid') },
    { id: '00s' as const, label: safeT('landing_hero.era_00s', '00s Youth') },
    { id: '10s' as const, label: safeT('landing_hero.era_10s', '10s Generation') },
  ];
  const eraSuffix = safeT('landing_hero.era_selected_suffix', 'timeline.');
  const eraSuffixSeparator = /^[.,!?]/.test(eraSuffix) ? '' : ' ';

  return (
    <section className="relative px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div
        className="mx-auto max-w-3xl rounded-3xl p-[1.5px] shadow-xl"
        style={{ background: rainbowBorderBackground('#ffffff') }}
      >
        <div className="rounded-[22px] bg-white p-6 text-center sm:p-10">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-violet-600">
            {safeT('landing_hero.era_eyebrow', 'A place to begin')}
          </p>
          <h2 className="mt-2 text-2xl font-black text-slate-950 sm:text-3xl">
            {safeT('landing_hero.era_title', 'What era shaped you?')}
          </h2>
          <div
            className="mt-5 grid gap-2 sm:grid-cols-3"
            role="group"
            aria-label={safeT('landing_hero.era_title', 'What era shaped you?')}
          >
            {eraOptions.map((era) => {
              const selected = era.id === selectedEra;
              return (
                <button
                  key={era.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setSelectedEra(era.id)}
                  className={`rounded-xl border px-4 py-3 text-sm font-bold transition-[transform,background-color,border-color] duration-150 ease-out active:scale-[0.98] ${
                    selected
                      ? 'border-violet-600 bg-violet-600 text-white shadow-lg'
                      : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                  }`}
                >
                  {era.label}
                </button>
              );
            })}
          </div>
          <p className="mt-4 text-sm font-semibold text-slate-600" aria-live="polite">
            {safeT('landing_hero.era_selected_prefix', 'Let’s build your')} {selectedEra}
            {eraSuffixSeparator}
            {eraSuffix}
          </p>
          <button
            type="button"
            onClick={() => router.push('/register')}
            className="mt-5 inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-black text-white shadow-lg transition-transform duration-150 ease-out active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg,#7c3aed,#2563eb)' }}
          >
            {safeT('landing_hero.era_cta', 'Build My Journey')}
            <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </section>
  );
}
